'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePathname } from 'next/navigation';

import { useNavigate } from '@hooks/useNavigation';
import { useSubscriptionManagement } from '@hooks/useSubscriptionManagement';
import { useTeacherListingData } from '@hooks/useTeacherListingData';
import Notification from '@icons/Notification.svg';
import { useHeaderVisibilityStore } from '@providers/header-visibility-store-provider';
import { ROLES } from 'types';

import { Button } from '@components/Button';
import { BottomContent, PageBottom, PageContent, PageGrid } from '@components/Common';
import { DailyAvailability } from '@components/DaySelectionPanel';
import { LoadingErrorHandler } from '@components/LoadingErrorHandler';

import { initializeSelectedDays } from '@utils/helpers';

import { AvailabilityModal } from './components/AvailabilityModal';
import { CompletedSchoolListing } from './components/CompletedSchoolListing';
import { NotificationConfirmationModal } from './components/NotificationConfirmationModal';
import { ShiftApplicationModal } from './components/ShiftApplicationModal';

interface TeacherListingProps {
  listingId: string;
}

/**
 * Teacher Listing component
 */
export const TeacherListing: React.FC<TeacherListingProps> = ({ listingId }) => {
  const { t } = useTranslation('common');
  const { goToPath } = useNavigate();
  const pathname = usePathname();
  const setShowBackButton = useHeaderVisibilityStore((state) => state.setShowBackButton);
  const setOnBack = useHeaderVisibilityStore((state) => state.setOnBack);

  // Data fetching and filtering
  const { filteredListing, teacher, currentSubscription, schoolName, isLoading, isError } =
    useTeacherListingData(listingId);

  // Local modal state
  const [openShiftModal, setOpenShiftModal] = useState(false);
  const [openAvailabilityModal, setOpenAvailabilityModal] = useState(false);
  const [notificationConfirmation, setNotificationConfirmation] = useState<{
    mode: 'created' | 'updated';
    selectedDays: DailyAvailability;
  } | null>(null);
  const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);

  const handleNotificationSaveSuccess = useCallback(
    ({
      mode,
      selectedDays,
    }: {
      mode: 'created' | 'updated';
      selectedDays: DailyAvailability;
    }) => {
      setOpenAvailabilityModal(false);
      setNotificationConfirmation({ mode, selectedDays });
    },
    [],
  );

  // Sends the teacher to their hulpverzoeken after confirming school notifications.
  const handleNotificationConfirmationConfirm = useCallback(() => {
    setNotificationConfirmation(null);
    goToPath(`/${ROLES.TEACHER}/shifts`);
  }, [goToPath]);

  // Subscription management
  const {
    selectedDays,
    handleToggleDay,
    handleSaveAvailability,
    handleUnsubscribe,
    setSelectedDays,
  } = useSubscriptionManagement(
    listingId,
    currentSubscription,
    teacher,
    handleNotificationSaveSuccess,
  );

  // Derived data
  const selectedShift = filteredListing?.shifts?.find((shift) => shift.id === selectedShiftId);

  // Header navigation setup
  const handleBack = useCallback(() => {
    goToPath(`/${ROLES.TEACHER}/overview`);
  }, [goToPath]);

  useEffect(() => {
    setShowBackButton(true);
    setOnBack(handleBack);

    return () => {
      setShowBackButton(false);
      setOnBack(undefined);
    };
  }, [setShowBackButton, setOnBack, pathname, handleBack]);

  // Modal handlers
  const handleOpenShiftModal = () => {
    setOpenShiftModal(true);
  };

  const handleCloseShiftModal = () => {
    setOpenShiftModal(false);
    setSelectedShiftId(null);
  };

  const handleOpenAvailabilityModal = () => {
    setOpenAvailabilityModal(true);
  };

  const handleCloseAvailabilityModal = () => {
    // Reset to saved state when closing without saving
    setSelectedDays(initializeSelectedDays(currentSubscription, teacher));
    setOpenAvailabilityModal(false);
  };

  // Main content rendering
  const renderListingContent = () => {
    if (filteredListing) {
      return (
        <CompletedSchoolListing
          selectedShiftId={selectedShiftId}
          setSelectedShiftId={setSelectedShiftId}
          schoolProfile={filteredListing?.user_school || null}
          listing={filteredListing}
          isTeacher={true}
        />
      );
    }
    return null;
  };

  // Bottom button logic - always show setNotificationSchool
  const getBottomButton = () => {
    // If a shift is selected, show the reaction button
    if (filteredListing && selectedShiftId) {
      return <Button label={t('schoolListing.reactToAssignment')} onClick={handleOpenShiftModal} />;
    }

    // Always show setNotificationSchool button for school listing
    return (
      <Button
        label={t('teacherOverview.setNotificationSchool')}
        onClick={handleOpenAvailabilityModal}
        icon={<Notification />}
      />
    );
  };

  return (
    <PageGrid className="full-height" $marginTopLarge={false}>
      <PageContent>
        <LoadingErrorHandler isLoading={isLoading} isError={isError}>
          {renderListingContent()}
        </LoadingErrorHandler>

        {/* Shift Application Modal */}
        <ShiftApplicationModal
          isOpen={openShiftModal}
          onClose={handleCloseShiftModal}
          selectedShift={selectedShift}
          selectedShiftId={selectedShiftId}
          schoolName={schoolName}
          teacher={teacher}
        />

        {/* Availability/Subscription Modal */}
        <AvailabilityModal
          isOpen={openAvailabilityModal}
          onClose={handleCloseAvailabilityModal}
          schoolName={schoolName}
          currentSubscription={currentSubscription}
          selectedDays={selectedDays}
          onToggleDay={handleToggleDay}
          onSaveAvailability={handleSaveAvailability}
          onUnsubscribe={handleUnsubscribe}
        />

        {notificationConfirmation && (
          <NotificationConfirmationModal
            onClose={() => setNotificationConfirmation(null)}
            onConfirm={handleNotificationConfirmationConfirm}
            mode={notificationConfirmation.mode}
            schoolName={schoolName}
            teacher={teacher}
            selectedDays={notificationConfirmation.selectedDays}
          />
        )}
      </PageContent>
      <PageBottom>
        <BottomContent>{getBottomButton()}</BottomContent>
      </PageBottom>
    </PageGrid>
  );
};
